import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PhoneInput } from "components/designV2/phoneInput/phoneInput";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import { SalaryType } from "model/Salary";
import { toEmploymentTypeDescription } from "web-apps/company/components/common/employmentType/employmentType.logic";
import {
  getSelectedOptionLabel,
  STAFF_TAX_TABLE_OPTIONS,
} from "../shared/staffPanel.options.logic";
import { StaffEmploymentEndDateField } from "../shared/staffEmploymentEndDateField.component";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "../shared/staffEmploymentFieldVisibility.logic";
import { EmploymentTypeDescription } from "../shared/staffEmploymentType.styled";
import { createIntegerInputHandlers } from "../shared/staffNumericInput.logic";
import type {
  AddStaffFormData,
  AddStaffPanelProps,
} from "./addStaffPanel.contract";
import { useAddStaffPanelLogic } from "./addStaffPanel.logic";
import { EmploymentTemplateMenu } from "./employmentTemplateMenu.component";
import type { AddStaffSectionName } from "./addStaffPanel.sections.logic";
import { toPaymentMethodHintTranslationKey } from "../shared/staff.paymentMethod.logic";
import {
  EmploymentTypeField,
  FieldsGrid,
  FooterWrapper,
  Section,
  SectionsWrapper,
  SubmitErrorText,
} from "./addStaffPanel.styled";
import { SectionBody, SectionHeader } from "./addStaffSection.component";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";

const PANEL_TITLE_ID = "add-staff-panel-title";

const FIRST_SECTION: AddStaffSectionName = "personal-details";

const toSectionBodyId = (section: AddStaffSectionName): string =>
  `add-staff-section-${section}`;

const toPaymentMethodTypeOrDefault = (
  type: string,
): AddStaffFormData["paymentMethodType"] => {
  if (type === "InternationalBankAccount") return "InternationalBankAccount";
  return "BankAccount";
};

export const AddStaffPanel = (
  props: AddStaffPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const toMandatoryLabel = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;
  const onClose = props.onClose;
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [openedSection, setOpenedSection] =
    useState<AddStaffSectionName | null>(FIRST_SECTION);
  const logic = useAddStaffPanelLogic({
    isOpen: props.isOpen,
    onClose: props.onClose,
    onWorkerCreated: props.onWorkerCreated,
    unitTeamOptions: props.unitTeamOptions,
    t,
  });
  const { isSubmitting, setField } = logic;
  const employmentTypeDescription = toEmploymentTypeDescription({
    employmentType: logic.formData.employmentType,
    translate: t,
  });

  const visibleSections = logic.visibleSections;
  const lockedSections = new Set(
    logic.areFormFieldsDisabled ? visibleSections.slice(1) : [],
  );
  const toggleSection = (section: AddStaffSectionName): void =>
    setOpenedSection((currentSection) =>
      currentSection === section ? null : section,
    );

  useEffect(() => {
    if (!props.isOpen) return;
    setOpenedSection(FIRST_SECTION);
  }, [props.isOpen]);

  useEffect(() => {
    if (props.isOpen) return;
    setIsDiscardConfirmOpen(false);
  }, [props.isOpen]);

  const closePanel = useCallback((): void => {
    setIsDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback((): void => {
    if (isSubmitting) return;
    if (logic.isFormStarted) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    closePanel();
  }, [closePanel, isSubmitting, logic.isFormStarted]);

  useEffect(() => {
    if (!props.isOpen || !logic.isFormStarted) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [props.isOpen, logic.isFormStarted]);

  useCloseOnEscape({
    isOpen: props.isOpen && !isSubmitting && !isDiscardConfirmOpen,
    onClose: requestClose,
    stopPropagation: true,
    preventDefault: true,
  });

  const onSetField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      setField(field, value);
    },
    [setField],
  );

  if (!props.isOpen) return null;

  const fieldVisibility = getEmploymentFieldVisibility(
    logic.formData.employmentType,
  );
  const vacationVisibility = getVacationFieldVisibility(
    logic.formData.salaryType,
  );
  const salaryTypeLabel = getSelectedOptionLabel({
    options: logic.salaryOptions,
    value: logic.formData.salaryType,
  });
  const paymentTypeLabel = getSelectedOptionLabel({
    options: logic.paymentOptions,
    value: logic.formData.paymentType,
  });
  const salaryTypeFieldLabel = logic.validationRequirements
    .isSalaryModelRequired
    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldSalaryModel"))
    : t("Staff.AddStaffPanelFieldSalaryModel");
  const paymentTypeFieldLabel = logic.validationRequirements
    .isPaymentTypeRequired
    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldPaymentSolution"))
    : t("Staff.AddStaffPanelFieldPaymentSolution");
  const noticeDaysHandlers = createIntegerInputHandlers(
    logic.formData.noticeDays,
    (value) => onSetField("noticeDays", value),
  );
  const vacationDaysHandlers = createIntegerInputHandlers(
    logic.formData.vacationDays,
    (value) => onSetField("vacationDays", value),
  );
  const maxSickLeaveDaysHandlers = createIntegerInputHandlers(
    logic.formData.maxSickLeaveDays,
    (value) => onSetField("maxSickLeaveDays", value),
  );
  const maxCareOfChildDaysHandlers = createIntegerInputHandlers(
    logic.formData.maxCareOfChildDays,
    (value) => onSetField("maxCareOfChildDays", value),
  );
  const maxParentalLeaveDaysHandlers = createIntegerInputHandlers(
    logic.formData.maxParentalLeaveDays,
    (value) => onSetField("maxParentalLeaveDays", value),
  );
  const advanceVacationDaysHandlers = createIntegerInputHandlers(
    logic.formData.advanceVacationDays,
    (value) => onSetField("advanceVacationDays", value),
  );

  const isAutofilled = (field: keyof AddStaffFormData): boolean =>
    logic.autofilledFields.has(field);

  return (
    <>
      <SidePanel
        isOpen={true}
        onClose={requestClose}
        onBackdropClick={requestClose}
        title={t("Staff.AddStaffPanelTitle")}
        titleId={PANEL_TITLE_ID}
        footer={
          <FooterWrapper>
            {logic.submitError.length > 0 && (
              <SubmitErrorText role="alert">
                {logic.submitError}
              </SubmitErrorText>
            )}
            <PrimaryButton
              label={t("Staff.AddStaffPanelActionAddWorker")}
              disabled={logic.isCreateDisabled}
              isLoading={logic.isSubmitting}
              onClick={logic.onAddWorkerClick}
            />
          </FooterWrapper>
        }
      >
        <SectionsWrapper>
          <Section>
            <SectionHeader
              title={t("Staff.AddStaffPanelSectionPersonalDetails")}
              isOpen={openedSection === "personal-details"}
              isCompleted={logic.sectionCompletion["personal-details"]}
              isLocked={lockedSections.has("personal-details")}
              bodyId={toSectionBodyId("personal-details")}
              onClick={() => {
                toggleSection("personal-details");
              }}
            />
            <SectionBody
              collapsed={openedSection !== "personal-details"}
              id={toSectionBodyId("personal-details")}
            >
              <FieldsGrid>
                <PhoneInput
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
                  value={logic.formData.phoneNumber}
                  isHighlighted={isAutofilled("phoneNumber")}
                  errorText={logic.phoneError}
                  hasError={logic.phoneError.length > 0}
                  onChange={logic.onPhoneNumberChange}
                  onBlur={logic.onPhoneNumberBlur}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPersonalNumber")}
                  hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
                  value={logic.formData.personalIdentityNumber}
                  isHighlighted={isAutofilled("personalIdentityNumber")}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.personalIdentityNumberError.length > 0}
                  errorText={logic.personalIdentityNumberError}
                  onChange={(value) =>
                    onSetField("personalIdentityNumber", value)
                  }
                  onBlur={logic.onPersonalIdentityNumberBlur}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldFirstNameMandatory")}
                  value={logic.formData.firstName}
                  isHighlighted={isAutofilled("firstName")}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.firstNameError.length > 0}
                  errorText={logic.firstNameError}
                  onChange={(value) => onSetField("firstName", value)}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldLastNameMandatory")}
                  value={logic.formData.lastName}
                  isHighlighted={isAutofilled("lastName")}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.lastNameError.length > 0}
                  errorText={logic.lastNameError}
                  onChange={(value) => onSetField("lastName", value)}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldEmail")}
                  value={logic.formData.email}
                  type="email"
                  isHighlighted={isAutofilled("email")}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.emailError.length > 0}
                  errorText={logic.emailError}
                  onChange={(value) => onSetField("email", value)}
                />
                <InputField
                  fullWidth
                  label={
                    logic.validationRequirements.isEmployeeNumberRequired
                      ? toMandatoryLabel(
                          t("Staff.AddStaffPanelFieldEmployeeNumber"),
                        )
                      : t("Staff.AddStaffPanelFieldEmployeeNumber")
                  }
                  value={logic.formData.employeeNumber}
                  hasError={logic.employeeNumberError.length > 0}
                  errorText={logic.employeeNumberError}
                  iconName="v2/reload"
                  iconAriaLabel={t(
                    "Staff.AddStaffPanelActionReloadEmployeeNumber",
                  )}
                  iconTabbable
                  onIconClick={logic.fetchEmployeeNumber}
                  disabled={
                    logic.isLoadingEmployeeNumber ||
                    logic.isSubmitting ||
                    logic.areFormFieldsDisabled
                  }
                  onChange={(value) => onSetField("employeeNumber", value)}
                />
              </FieldsGrid>
            </SectionBody>
          </Section>
          <Section>
            <SectionHeader
              title={t("Staff.AddStaffPanelSectionEmployment")}
              isOpen={openedSection === "contract"}
              isCompleted={logic.sectionCompletion["contract"]}
              isLocked={lockedSections.has("contract")}
              bodyId={toSectionBodyId("contract")}
              onClick={() => {
                toggleSection("contract");
              }}
            >
              {openedSection === "contract" && (
                <EmploymentTemplateMenu
                  options={logic.employmentTemplateMenuOptions}
                  isLoading={logic.isLoadingEmploymentTemplateMenu}
                  disabled={logic.areFormFieldsDisabled}
                  onSelect={logic.applyEmploymentTemplateSelection}
                />
              )}
            </SectionHeader>
            <SectionBody
              collapsed={openedSection !== "contract"}
              id={toSectionBodyId("contract")}
            >
              <FieldsGrid>
                <InputField
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"))}
                  value={logic.formData.role}
                  isHighlighted={isAutofilled("role")}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.roleError.length > 0}
                  errorText={logic.roleError}
                  onChange={(value) => onSetField("role", value)}
                />
                <SearchableDropdown
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldTeam"))}
                  options={logic.teamOptions}
                  selectedValues={logic.formData.teamIds}
                  multiSelect
                  disabled={logic.areFormFieldsDisabled}
                  onSelectedValuesChange={(value) =>
                    onSetField("teamIds", value)
                  }
                  placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
                />
                <DatePicker
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldStartDate"),
                  )}
                  value={logic.formData.startDate}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) => onSetField("startDate", value)}
                  allowClear
                />
                <StaffEmploymentEndDateField
                  allowClear={!logic.validationRequirements.isEndDateRequired}
                  disabled={logic.areFormFieldsDisabled}
                  employmentType={logic.formData.employmentType}
                  endDate={logic.formData.endDate}
                  label={
                    logic.validationRequirements.isEndDateRequired
                      ? toMandatoryLabel(t("Staff.AddStaffPanelFieldEndDate"))
                      : t("Staff.AddStaffPanelFieldEndDate")
                  }
                  startDate={logic.formData.startDate}
                  onChange={(value) => onSetField("endDate", value)}
                />
                <EmploymentTypeField>
                  <Dropdown
                    fullWidth
                    label={toMandatoryLabel(
                      t("Staff.AddStaffPanelFieldEmploymentType"),
                    )}
                    options={logic.employmentOptions}
                    value={logic.formData.employmentType}
                    isHighlighted={isAutofilled("employmentType")}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("employmentType", value)}
                    placeholder={t(
                      "Staff.AddStaffPanelPlaceholderEmploymentType",
                    )}
                  />
                  {employmentTypeDescription && (
                    <EmploymentTypeDescription>
                      {employmentTypeDescription}
                    </EmploymentTypeDescription>
                  )}
                </EmploymentTypeField>
                {fieldVisibility.showEmployeeCategory && (
                  <Dropdown
                    fullWidth
                    label={
                      logic.validationRequirements.isEmployeeCategoryRequired
                        ? toMandatoryLabel(
                            t("Staff.AddStaffPanelFieldStaffType"),
                          )
                        : t("Staff.AddStaffPanelFieldStaffType")
                    }
                    options={logic.employeeCategoryOptions}
                    value={logic.formData.employeeCategory}
                    isHighlighted={isAutofilled("employeeCategory")}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("employeeCategory", value)}
                  />
                )}
                {fieldVisibility.showSalaryType &&
                  (logic.salaryOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={salaryTypeFieldLabel}
                      value={salaryTypeLabel}
                      isHighlighted={isAutofilled("salaryType")}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={salaryTypeFieldLabel}
                      options={logic.salaryOptions}
                      value={logic.formData.salaryType}
                      isHighlighted={isAutofilled("salaryType")}
                      disabled={logic.areFormFieldsDisabled}
                      onChange={(value) => onSetField("salaryType", value)}
                      placeholder={t(
                        "Staff.AddStaffPanelPlaceholderSalaryModel",
                      )}
                    />
                  ))}
                {fieldVisibility.showSalary && (
                  <InputField
                    fullWidth
                    type="number"
                    label={
                      logic.validationRequirements.isSalaryAmountRequired
                        ? toMandatoryLabel(logic.salaryAmountLabel)
                        : logic.salaryAmountLabel
                    }
                    value={logic.formData.salaryAmount}
                    isHighlighted={isAutofilled("salaryAmount")}
                    placeholder={t(
                      "Staff.AddStaffPanelPlaceholderSalaryAmount",
                    )}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("salaryAmount", value)}
                  />
                )}
                {fieldVisibility.showSalaryType &&
                  logic.formData.salaryType === SalaryType.Monthly && (
                    <InputField
                      fullWidth
                      type="number"
                      label={
                        logic.validationRequirements.isWorkPercentageRequired
                          ? toMandatoryLabel(
                              `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                            )
                          : `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`
                      }
                      value={logic.formData.workPercentage}
                      isHighlighted={isAutofilled("workPercentage")}
                      disabled={logic.areFormFieldsDisabled}
                      hasError={logic.workPercentageError.length > 0}
                      errorText={logic.workPercentageError}
                      onChange={(value) => onSetField("workPercentage", value)}
                    />
                  )}
                {fieldVisibility.showTaxTable && (
                  <Dropdown
                    fullWidth
                    label={
                      logic.validationRequirements.isTaxTableRequired
                        ? toMandatoryLabel(
                            t("Staff.AddStaffPanelFieldTaxTable"),
                          )
                        : t("Staff.AddStaffPanelFieldTaxTable")
                    }
                    options={STAFF_TAX_TABLE_OPTIONS}
                    value={logic.formData.taxTable}
                    disabled={
                      logic.isLoadingTaxTable || logic.areFormFieldsDisabled
                    }
                    onChange={(value) => onSetField("taxTable", value)}
                  />
                )}
                {vacationVisibility.showVacationPayoutType && (
                  <Dropdown
                    fullWidth
                    label={t("Staff.AddStaffPanelFieldVacationPayoutType")}
                    options={logic.vacationPayoutOptions}
                    value={logic.formData.vacationPayoutType}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) =>
                      onSetField("vacationPayoutType", value)
                    }
                  />
                )}
              </FieldsGrid>
            </SectionBody>
          </Section>
          {fieldVisibility.showContractTerms && (
            <Section>
              <SectionHeader
                title={t("Staff.AddStaffPanelSectionAgreementsTerms")}
                isOpen={openedSection === "terms"}
                isCompleted={logic.sectionCompletion["terms"]}
                isLocked={lockedSections.has("terms")}
                bodyId={toSectionBodyId("terms")}
                onClick={() => {
                  toggleSection("terms");
                }}
              />
              <SectionBody
                collapsed={openedSection !== "terms"}
                id={toSectionBodyId("terms")}
              >
                <FieldsGrid>
                  <InputField
                    fullWidth
                    label={t(
                      "Staff.AddStaffPanelFieldCollectiveSalaryAgreement",
                    )}
                    value={logic.formData.agreement}
                    isHighlighted={isAutofilled("agreement")}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.agreementError.length > 0}
                    errorText={logic.agreementError}
                    onChange={(value) => onSetField("agreement", value)}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    value={logic.formData.noticeDays}
                    isHighlighted={isAutofilled("noticeDays")}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.noticeDaysError.length > 0}
                    errorText={logic.noticeDaysError}
                    onChange={noticeDaysHandlers.onChange}
                    onBlur={noticeDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldVacationDays")}
                    value={logic.formData.vacationDays}
                    isHighlighted={isAutofilled("vacationDays")}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.vacationDaysError.length > 0}
                    errorText={logic.vacationDaysError}
                    onChange={vacationDaysHandlers.onChange}
                    onBlur={vacationDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    label={t("Staff.AddStaffPanelFieldWorkPlace")}
                    value={logic.formData.workplace}
                    isHighlighted={isAutofilled("workplace")}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.workplaceError.length > 0}
                    errorText={logic.workplaceError}
                    onChange={(value) => onSetField("workplace", value)}
                  />
                </FieldsGrid>
              </SectionBody>
            </Section>
          )}
          {vacationVisibility.showVacationEarningDays && (
            <Section>
              <SectionHeader
                title={t("Staff.AddStaffPanelSectionVacation")}
                isOpen={openedSection === "vacation"}
                isCompleted={logic.sectionCompletion["vacation"]}
                isLocked={lockedSections.has("vacation")}
                bodyId={toSectionBodyId("vacation")}
                onClick={() => {
                  toggleSection("vacation");
                }}
              />
              <SectionBody
                collapsed={openedSection !== "vacation"}
                id={toSectionBodyId("vacation")}
              >
                <FieldsGrid>
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldMaxSickLeaveDays")}
                    value={logic.formData.maxSickLeaveDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.maxSickLeaveDaysError.length > 0}
                    errorText={logic.maxSickLeaveDaysError}
                    onChange={maxSickLeaveDaysHandlers.onChange}
                    onBlur={maxSickLeaveDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldMaxCareOfChildDays")}
                    value={logic.formData.maxCareOfChildDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.maxCareOfChildDaysError.length > 0}
                    errorText={logic.maxCareOfChildDaysError}
                    onChange={maxCareOfChildDaysHandlers.onChange}
                    onBlur={maxCareOfChildDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldMaxParentalLeaveDays")}
                    value={logic.formData.maxParentalLeaveDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.maxParentalLeaveDaysError.length > 0}
                    errorText={logic.maxParentalLeaveDaysError}
                    onChange={maxParentalLeaveDaysHandlers.onChange}
                    onBlur={maxParentalLeaveDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldAdvanceVacationDays")}
                    value={logic.formData.advanceVacationDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.advanceVacationDaysError.length > 0}
                    errorText={logic.advanceVacationDaysError}
                    onChange={advanceVacationDaysHandlers.onChange}
                    onBlur={advanceVacationDaysHandlers.onBlur}
                  />
                </FieldsGrid>
              </SectionBody>
            </Section>
          )}
          <Section>
            <SectionHeader
              title={t("Staff.AddStaffPanelSectionPaymentDetails")}
              isOpen={openedSection === "payment"}
              isCompleted={logic.sectionCompletion["payment"]}
              isLocked={lockedSections.has("payment")}
              bodyId={toSectionBodyId("payment")}
              onClick={() => {
                toggleSection("payment");
              }}
            />
            <SectionBody
              collapsed={openedSection !== "payment"}
              id={toSectionBodyId("payment")}
            >
              <FieldsGrid>
                <Dropdown
                  fullWidth
                  label={t("Staff.PaymentMethodType")}
                  options={logic.paymentMethodOptions}
                  value={logic.formData.paymentMethodType}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) =>
                    onSetField(
                      "paymentMethodType",
                      toPaymentMethodTypeOrDefault(value),
                    )
                  }
                />
                <InputField
                  fullWidth
                  label={t("Staff.PaymentMethodValue")}
                  value={logic.formData.paymentMethodValue}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.paymentMethodError.length > 0}
                  hintText={t(
                    toPaymentMethodHintTranslationKey(
                      logic.formData.paymentMethodType,
                    ),
                  )}
                  errorText={logic.paymentMethodError}
                  onChange={(value) => onSetField("paymentMethodValue", value)}
                />
                {fieldVisibility.showPaymentType &&
                  (logic.paymentOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={paymentTypeFieldLabel}
                      value={paymentTypeLabel}
                      isHighlighted={isAutofilled("paymentType")}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={paymentTypeFieldLabel}
                      options={logic.paymentOptions}
                      value={logic.formData.paymentType}
                      isHighlighted={isAutofilled("paymentType")}
                      disabled={logic.areFormFieldsDisabled}
                      onChange={(value) => onSetField("paymentType", value)}
                    />
                  ))}
              </FieldsGrid>
            </SectionBody>
          </Section>
        </SectionsWrapper>
      </SidePanel>
      <Modal
        variant="confirm"
        label={t("Staff.AddStaffPanelDiscardConfirmTitle")}
        size="compact"
        showCloseButton={false}
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => setIsDiscardConfirmOpen(false)}
            />
            <PrimaryButton
              label={t("Staff.AddStaffPanelDiscardConfirmAction")}
              onClick={closePanel}
            />
          </>
        }
      >
        {t("Staff.AddStaffPanelDiscardConfirmBody")}
      </Modal>
    </>
  );
};
